import React, { useEffect, useMemo, useRef, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Table } from 'ui/Table';
import { ITableColumn, ITableRow } from 'ui/Table/types';
import { ESortOrder } from 'store/common/types';
import { CircleIconButton } from 'ui/CircleIconButton';
import FileIcon from 'ui/Icons/tabler/file.component.svg';
import PencilIcon from 'ui/Icons/tabler/pencil.component.svg';
import ArrowRightIcon from 'ui/Icons/tabler/arrow-right.component.svg';
import { IInvoiceUpload, TInvoiceUploadTab } from '../types';
import { parseAmountToCents, parseConfidencePercent } from 'services/BookingManagerApi/invoiceUploads';
import { useInvoiceUploadFileUrlQuery } from '../hooks/useInvoiceUploadFileUrlQuery';
import { ConfidenceBar } from './ConfidenceBar';
import { ErrorsCell } from './ErrorsCell';
import { StatusCell } from './StatusCell';
import ChevronRightIcon from 'ui/Icons/tabler/chevron-right.component.svg';

interface IInvoiceUploadsTableProps {
  tab: TInvoiceUploadTab;
  rows: IInvoiceUpload[];
  loading: boolean;
  sortOrder: ESortOrder;
  onEdit?: (row: IInvoiceUpload) => void;
  onGoToDetail?: (row: IInvoiceUpload) => void;
}

const InvoiceFilePreviewButton: React.FC<{ importUuid: string }> = ({ importUuid }) => {
  const [shouldFetch, setShouldFetch] = useState(false);
  const { data, isFetching } = useInvoiceUploadFileUrlQuery(shouldFetch ? importUuid : null);
  const hasOpened = useRef(false);

  useEffect(() => {
    if (data?.url && !hasOpened.current) {
      hasOpened.current = true;
      window.open(data.url, '_blank');
      setShouldFetch(false);
    }
  }, [data?.url]);

  return (
    <CircleIconButton
      type="secondary"
      IconComponent={FileIcon}
      disabled={isFetching}
      onClick={() => {
        hasOpened.current = false;
        setShouldFetch(true);
      }}
    />
  );
};

const formatCreated = (iso: string): React.ReactNode => (
  <span className="whitespace-nowrap">
    {format(parseISO(iso), 'dd MMM yy')},
    <br />
    {format(parseISO(iso), 'HH:mm')}
  </span>
);

const formatDateShort = (iso: string | null | undefined): string => (iso ? format(parseISO(iso), 'dd MMM yy') : '');

const formatAmount = (cents: number | null): string => {
  if (cents == null) return '';
  return new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100);
};

const placeholder = <span className="text-gray-60">---</span>;
const noop = (): void => undefined;
const valueOr = (value: React.ReactNode | null | undefined) =>
  value === null || value === undefined || value === '' ? placeholder : value;

export const InvoiceUploadsTable: React.FC<IInvoiceUploadsTableProps> = ({
  tab,
  rows,
  loading,
  sortOrder,
  onEdit,
  onGoToDetail,
}) => {
  const columns: ITableColumn[] = useMemo(() => {
    const common: ITableColumn[] = [
      { id: 'createdAt', label: 'Created', width: '95px', fieldToSortBy: 'createdAt' },
      { id: 'invoiceDate', label: 'Invoice', width: '85px' },
      { id: 'description', label: 'Description', width: '120px' },
      { id: 'supplier', label: 'Supplier', width: '140px' },
      { id: 'confirmationNumber', label: 'Confirm N.', width: '100px' },
      { id: 'bookingRef', label: 'Booking', width: '130px' },
      { id: 'currency', label: 'Currency', width: '80px' },
      { id: 'amount', label: 'Amount', width: '110px', className: 'text-right' },
      { id: 'confidence', label: 'Confidence', width: '90px' },
    ];
    const last: ITableColumn[] =
      tab === 'pending'
        ? [
            { id: 'errors', label: 'Errors', width: '90px' },
            { id: 'actions', label: '', width: '90px' },
          ]
        : [
            { id: 'status', label: '', width: '50px' },
            { id: 'actions', label: '', width: '90px' },
          ];
    return [...common, ...last];
  }, [tab]);

  const tableRows: ITableRow[] = useMemo(
    () =>
      rows.map(row => {
        const errorCount = row.errors?.length ?? 0;
        const amountCents = parseAmountToCents(row.amount);
        const confidencePercent = parseConfidencePercent(row.confidence);
        const baseCells = [
          { id: 'createdAt', value: formatCreated(row.createdAt) },
          { id: 'invoiceDate', value: valueOr(formatDateShort(row.invoiceDate)) },
          { id: 'description', value: valueOr(row.invoiceNumber) },
          { id: 'supplier', value: valueOr(row.supplierName) },
          { id: 'confirmationNumber', value: valueOr(row.confirmationNumber) },
          { id: 'bookingRef', value: valueOr(row.bookingReference) },
          { id: 'currency', value: valueOr(row.currency) },
          {
            id: 'amount',
            value: amountCents == null ? placeholder : formatAmount(amountCents),
            className: 'text-right',
          },
          { id: 'confidence', value: <ConfidenceBar value={confidencePercent ?? 0} /> },
        ];

        const tailCells =
          tab === 'pending'
            ? [
                {
                  id: 'errors',
                  value: <ErrorsCell errorCount={errorCount} />,
                },
                {
                  id: 'actions',
                  value: (
                    <div className="flex items-center gap-2">
                      <InvoiceFilePreviewButton importUuid={row.uuid} />
                      <CircleIconButton
                        type="secondary"
                        IconComponent={PencilIcon}
                        onClick={() => onEdit?.(row)}
                      />
                    </div>
                  ),
                },
              ]
            : [
                { id: 'status', value: <StatusCell status={row.status} /> },
                {
                  id: 'actions',
                  value: (
                    <div className="flex items-center gap-2">
                      <InvoiceFilePreviewButton importUuid={row.uuid} />
                      <CircleIconButton
                        type="secondary"
                        IconComponent={ChevronRightIcon}
                        onClick={() => onGoToDetail?.(row)}
                      />
                    </div>
                  ),
                },
              ];

        return {
          id: row.uuid,
          cells: [...baseCells, ...tailCells],
        };
      }),
    [rows, tab, onEdit, onGoToDetail]
  );

  return (
    <Table
      columns={columns}
      rows={tableRows}
      sortBy="createdAt"
      sortOrder={sortOrder}
      onSort={noop}
      loading={loading}
      messageWhenEmpty="No Results"
      className="mt-4"
      evenColor="white-hard"
    />
  );
};
